"use client";
import Image from "@/components/AppImage";
import { PaylioContext } from "@/context/context";
import { useReceiveFunding } from "@/hooks/useReceiveFunding";
import {
  EXCHANGE_ASSETS,
  estimateConversion,
  getExchangeAsset,
  usdFromNetCrypto,
} from "@/data/exchangeAssets";
import { useContext, useMemo, useState } from "react";
import { BsLightningChargeFill } from "react-icons/bs";
import support_icon from "/public/images/icon/support-icon.png";

function formatHeaderDate() {
  return new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function parseDecimalInput(raw) {
  const n = parseFloat(String(raw).replace(/,/g, "").trim());
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function formatUsdStr(n) {
  if (!Number.isFinite(n) || n < 0) return "0.00";
  return n.toFixed(2);
}

function formatCryptoStr(n) {
  if (!Number.isFinite(n) || n < 0) return "0";
  let s = n.toFixed(8);
  s = s.replace(/\.?0+$/, "");
  return s || "0";
}

function truncateAddr(addr) {
  if (!addr || typeof addr !== "string") return "—";
  const t = addr.trim();
  if (t.length <= 14) return t;
  return `${t.slice(0, 6)}…${t.slice(-4)}`;
}

const StepOne = () => {
  const { activeLefMenu } = useContext(PaylioContext);
  const { data: funding } = useReceiveFunding();
  const [drive, setDrive] = useState("usd");
  const [usdInput, setUsdInput] = useState("5000.00");
  const [cryptoInput, setCryptoInput] = useState(() => {
    const a = EXCHANGE_ASSETS[0];
    const { net } = estimateConversion(5000, a);
    return formatCryptoStr(net);
  });
  const [assetId, setAssetId] = useState(EXCHANGE_ASSETS[0].id);

  const asset = useMemo(() => getExchangeAsset(assetId), [assetId]);

  const usdAmount = useMemo(() => parseDecimalInput(usdInput), [usdInput]);

  const est = useMemo(
    () => estimateConversion(usdAmount, asset),
    [usdAmount, asset],
  );

  const handleUsdChange = (e) => {
    const v = e.target.value;
    setUsdInput(v);
    setDrive("usd");
    const u = parseDecimalInput(v);
    const { net } = estimateConversion(u, asset);
    setCryptoInput(formatCryptoStr(net));
  };

  const handleCryptoChange = (e) => {
    const v = e.target.value;
    setCryptoInput(v);
    setDrive("crypto");
    const net = parseDecimalInput(v);
    const usd = usdFromNetCrypto(net, asset);
    setUsdInput(formatUsdStr(usd));
  };

  const handleAssetChange = (e) => {
    const id = e.target.value;
    const next = getExchangeAsset(id);
    setAssetId(id);
    if (drive === "usd") {
      const u = parseDecimalInput(usdInput);
      const { net } = estimateConversion(u, next);
      setCryptoInput(formatCryptoStr(net));
    } else {
      const net = parseDecimalInput(cryptoInput);
      const usd = usdFromNetCrypto(net, next);
      setUsdInput(formatUsdStr(usd));
    }
  };

  const dest =
    funding?.crypto?.destinationAddress?.trim() ||
    "0x742d35Cc6634C0532925a3b844Bc454e4438f44e";
  const destDisplay = truncateAddr(dest);

  return (
    <section
      className={`dashboard-section receive-flowpay exchange-flowpay ${
        activeLefMenu ? "body-collapse" : ""
      }`}>
      <div className="overlay pt-120">
        <div className="container-fruid">
          <div className="main-content">
            <div className="fp-ex">
              <header className="fp-ex__header">
                <div>
                  <h1 className="fp-ex__title">Exchange</h1>
                  <p className="fp-ex__date">{formatHeaderDate()}</p>
                </div>
                <div className="fp-recv-landing__support">
                  <Image src={support_icon} alt="" />
                </div>
              </header>

              <div className="fp-ex__banner" role="status">
                <span className="fp-ex__banner-icon" aria-hidden>
                  i
                </span>
                <p className="fp-ex__banner-text">
                  FlowPay auto-converts incoming USD at the live market rate the moment funds arrive.
                  This page shows live reference rates so you know what to expect.
                </p>
              </div>

              <div className="fp-ex__grid">
                <div className="fp-ex__panel">
                  <h2 className="fp-ex__panel-title">Live conversion rates (USD →)</h2>
                  {EXCHANGE_ASSETS.map((a) => (
                    <div key={a.id} className="fp-ex__rate-row">
                      <div className="fp-ex__rate-icon" aria-hidden>
                        {a.iconLetter}
                      </div>
                      <div className="fp-ex__rate-main">
                        <span className="fp-ex__rate-name">{a.label}</span>
                        <span className="fp-ex__rate-sub">1 USD</span>
                      </div>
                      <div className="fp-ex__rate-right">
                        <div className="fp-ex__rate-value">
                          {a.tokensPerUsd.toFixed(6)}
                        </div>
                        <div
                          className={`fp-ex__rate-chg ${
                            a.changePct >= 0
                              ? "fp-ex__rate-chg--up"
                              : "fp-ex__rate-chg--down"
                          }`}>
                          {a.changePct >= 0 ? "+" : ""}
                          {a.changePct.toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div>
                  <div className="fp-ex__panel">
                    <h2 className="fp-ex__panel-title">Estimate a conversion</h2>

                    <label className="fp-ex__label" htmlFor="fp-ex-usd">
                      If I receive (USD)
                    </label>
                    <div className="fp-ex__field-wrap">
                      <span className="fp-ex__field-prefix">$</span>
                      <input
                        id="fp-ex-usd"
                        className="fp-ex__input"
                        inputMode="decimal"
                        autoComplete="off"
                        value={usdInput}
                        onChange={handleUsdChange}
                        aria-describedby="fp-ex-estimate-help"
                      />
                    </div>

                    <label className="fp-ex__label" htmlFor="fp-ex-asset">
                      Asset
                    </label>
                    <select
                      id="fp-ex-asset"
                      className="fp-ex__select"
                      value={assetId}
                      onChange={handleAssetChange}>
                      {EXCHANGE_ASSETS.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.label}
                        </option>
                      ))}
                    </select>

                    <label className="fp-ex__label" htmlFor="fp-ex-crypto">
                      Net amount ({asset.short})
                    </label>
                    <div className="fp-ex__field-wrap fp-ex__field-wrap--suffix">
                      <input
                        id="fp-ex-crypto"
                        className="fp-ex__input"
                        inputMode="decimal"
                        autoComplete="off"
                        value={cryptoInput}
                        onChange={handleCryptoChange}
                        aria-describedby="fp-ex-estimate-help"
                      />
                      <span className="fp-ex__field-suffix">{asset.short}</span>
                    </div>

                    <p id="fp-ex-estimate-help" className="visually-hidden">
                      Edit USD or net crypto; values stay in sync using the reference rate and fee
                      for the selected asset.
                    </p>

                    <div className="fp-ex__summary">
                      <div className="fp-ex__summary-row">
                        <span className="fp-ex__summary-k">Rate</span>
                        <span className="fp-ex__summary-v">
                          1 USD = {asset.tokensPerUsd.toFixed(6)} {asset.short}
                        </span>
                      </div>
                      <div className="fp-ex__summary-row">
                        <span className="fp-ex__summary-k">Conversion fee</span>
                        <span className="fp-ex__summary-v">
                          {(asset.feeFraction * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="fp-ex__summary-row">
                        <span className="fp-ex__summary-k">Net crypto</span>
                        <span className="fp-ex__summary-v fp-ex__summary-v--net">
                          ≈{" "}
                          {est.net.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 6,
                          })}{" "}
                          {asset.short}
                        </span>
                      </div>
                      <div className="fp-ex__summary-row">
                        <span className="fp-ex__summary-k">Time to wallet</span>
                        <span className="fp-ex__summary-v fp-ex__summary-v--time">
                          &lt; 30 seconds
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="fp-ex__rule">
                    <div className="fp-ex__rule-main">
                      <div className="fp-ex__rule-icon" aria-hidden>
                        <BsLightningChargeFill />
                      </div>
                      <div>
                        <p className="fp-ex__rule-line1">
                          100% USD → {asset.label}, instantly
                        </p>
                        <p className="fp-ex__rule-line2">→ {destDisplay}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="fp-ex__rule-edit"
                      disabled
                      aria-disabled="true">
                      Edit
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StepOne;
